import { useEffect, useState } from "react"
import { Button, Modal } from "react-bootstrap"
import DataTable from "react-data-table-component"
import * as DateFns from 'date-fns'
import CustomAlertModal from "./custom-alert-modal"
import useFetch from "../../../../_fetch"
import { IFilterSelectorOpts, IFilterSelectorOpts_Selected } from "../../home/dashboard-content/types"
import { FaEdit } from "react-icons/fa"

export type IAlertType = "perfomance_change"
export type ICustomAlert = {
    id: string
    name: string
    type: IAlertType
    direction: "increase" | "decrease"
    percentage: number
    comparisonPeriod: string
    metric: "revenue" | "commission" | "clicks"
    filters: IFilterSelectorOpts_Selected
    created: string
    disabled?:boolean
}

export default function CustomAlerts() {
    const [alerts, setAlerts] = useState<ICustomAlert[]>([])
    const [showModal, setShowModal] = useState(false)
    const [selectedAlert, setSelectedAlert] = useState<ICustomAlert | null>(null)
    const [errorModal, setErrorModal] = useState<{ show: boolean; message: string }>({ show: false, message: "" })
    const [info, setInfo] = useState<IFilterSelectorOpts>({campaigns:[], countries:[], networks:[], publishers: [], channels: []})
    const { doFetch } = useFetch()

    useEffect(() => {
        (async () => {
            const res = await doFetch('/data/relation-info')
            if (res.status === 200) {
                const data = await res.json()
                const countries = (data.countries as {country: string}[]).map(x => x.country)
                const networks = (data.networks as {network: string}[]).map(x => x.network)
                setInfo({campaigns:data.campaigns, countries, networks, publishers: data.publishers, channels: data.channels})
            }
            const res2 = await doFetch('/alerts/get-custom-alerts')
            if(res2.status === 200) {
                const data = await res2.json()
                setAlerts(data)
            }
        })()
    }, [])

    const handleCreate = () => {
        setSelectedAlert(null)
        setShowModal(true)
    }

    const handleEdit = (alert: ICustomAlert) => {
        setSelectedAlert(alert)
        setShowModal(true)
    }

    const handleSave = async (newAlert: Omit<ICustomAlert, 'id'|'created'> & {id?: string; created?: string} ) => {
        try {
            const updatedAlert: ICustomAlert = {
                ...newAlert,
                created: newAlert.created ? newAlert.created : new Date().toISOString(),
                id: newAlert.id ? newAlert.id : Date.now().toString(),
            }

            const res = await doFetch('/alerts/save-custom-alert', {alert: updatedAlert})
            if(res.status !== 200) throw new Error(await res.text())

            setAlerts(prev => {
                const existingIndex = prev.findIndex(a => a.id === updatedAlert.id)
                if (existingIndex >= 0) {
                    const newList = [...prev]
                    newList[existingIndex] = updatedAlert
                    return newList
                }
                return [...prev, updatedAlert]
            })
        } catch(err: any) {
            console.error(err)
            setErrorModal({
                show: true,
                message: "An unexpected error occurred while saving.",
            })
        }
    }

    const columns = [
        {
            name: "Name",
            selector: (row: ICustomAlert) => row.name,
            sortable: true,
            format: (row: ICustomAlert) => {
                return (
                    <>
                        <a href="#" onClick={(e) => { e.preventDefault(); handleEdit(row); }}>
                            <FaEdit className="me-2" />
                        </a>
                        {row.name}
                        {row.disabled && (
                            <span className="badge bg-secondary ms-2">Disabled</span>
                        )}
                    </>
                )
            }
        },
        {
            name: "Type",
            selector: (row: ICustomAlert) => row.type,
            sortable: true,
            format: (row: ICustomAlert) => row.type === 'perfomance_change' ? 'Performance change' : row.type
        },
        {
            name: "Info",
            selector: (row: ICustomAlert) => row.percentage,
            format: (row: ICustomAlert)=> {
                return `${row.direction === "increase" ? "Increase" : "Decrease"} by ${row.percentage}%`
            },
            sortable: true,
        },
        {
            name: "Created",
            selector: (row: ICustomAlert) => row.created,
            format: (row: ICustomAlert) => DateFns.format(new Date(row.created), 'yyyy-MM-dd HH:mm:ss'),
            sortable: true,
        },
    ]

    return (
        <div className="p-3">
            <div className="mb-3">
                <Button variant="primary" onClick={handleCreate}>Create new custom alert</Button>
            </div>

            <DataTable
                columns={columns}
                data={alerts}
                pagination
                highlightOnHover
            />

            <CustomAlertModal
                show={showModal}
                onClose={() => setShowModal(false)}
                onSave={handleSave}
                info={info}
                existingAlert={selectedAlert ?? undefined}
                onRemove={async (id) => {
                    try {
                        const res = await doFetch('/alerts/delete-custom-alert', {alertId: id})
                        if(res.status !== 200) throw new Error(await res.text())
                        setAlerts(prev => prev.filter(a => a.id !== id))
                    } catch(err) {
                        console.error(err)
                        setErrorModal({
                            show: true,
                            message: "An unexpected error occurred while deleting.",
                        })
                    }
                }}
            />

            <Modal show={errorModal.show} onHide={() => setErrorModal({ show: false, message: "" })}>
                <Modal.Header closeButton>
                    <Modal.Title>Save failed</Modal.Title>
                </Modal.Header>
                <Modal.Body>{errorModal.message}</Modal.Body>
                <Modal.Footer>
                    <Button variant="secondary" onClick={() => setErrorModal({ show: false, message: "" })}>
                        Close
                    </Button>
                </Modal.Footer>
            </Modal>
        </div>
    )
}
